import React from 'react';

import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';

import { FORMS } from '../../schemas/stepMap';
import { useSteps } from '../../hooks/useSteps';
import BackForwardButtons from '../../components/buttons/BackForwardButtons';
import BRRRRKeyMetrics from './BRRRRKeyMetrics';
import BRRRRProjectionsSummary from './BRRRRProjectionsSummary';
import BRRRRSummaryOverview from './BRRRRSummaryOverview';
import FormBuilder from '../../components/formBuilder';

import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import getCurrentDateFormatted from '../../utility/dates';
import { Logo } from '../../components';
import formatAddress from '../../utility/formatAddress';

export default function PurchaseSummaryView() {
  const { prev, next } = useSteps();
  const { pathname } = useLocation();

  const BRRRRAnalysisForm = FORMS.BRRRRAnalysisSimple;
  const BRRRRAnalysisSchema = BRRRRAnalysisForm.schema;

  const { propertyData, scenarios } = useOutletContext();
  const scenarioData = scenarios.find((scenario) => !scenario.isDefault);

  if (!scenarioData?.refinancingWorksheetSubmitted) {
    return (
      <div className="w-full flex flex-col items-center justify-center mt-36">
        <h2 className="mb-6 text-xl font-medium text-primary">
          No BRRRR Analysis Created
        </h2>
        {!pathname.includes('sharing') && (
          <p className="mb-72 w-2/3 text-center">
            Once you have filled out the{' '}
            <Link
              to="../../purchase-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              purchase worksheet
            </Link>
            , the{' '}
            <Link
              to="../../rehab-worksheet"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              rehab worksheet
            </Link>
            , and the{' '}
            <Link
              to="../../refinancing"
              className="font-medium text-primary hover:text-primary-700 focus:text-primary-700"
            >
              refinancing worksheet
            </Link>
            , you will be able to see your BRRRR analysis here.
          </p>
        )}
      </div>
    );
  }

  function onReportClick(some) {
    generateCashflowReport();
  }

  function generateCashflowReport() {
    const element = document.getElementById('cash-flow-report');

    if (!element) {
      return;
    }

    try {
      const clonedElement = element.cloneNode(true);
      clonedElement.style.position = 'absolute';
      clonedElement.style.top = '-9999px';
      clonedElement.style.left = '-9999px';
      clonedElement.classList.add('printing');
      clonedElement.classList.add('p-8');
      clonedElement.style.width = '1080px';
      clonedElement.classList.remove('hidden');
      const h = document.getElementById('cashflow-printable-header');

      const header = h.cloneNode(true);

      header.classList.remove('hidden');
      header.classList.add('grid');
      clonedElement.insertBefore(header, clonedElement.firstChild);

      document.body.appendChild(clonedElement);

      html2canvas(clonedElement, {
        scale: 2,
      }).then((canvas) => {
        const componentWidth = clonedElement.offsetWidth;
        const componentHeight = clonedElement.offsetHeight;

        document.body.removeChild(clonedElement);
        const orientation = componentWidth >= componentHeight ? 'l' : 'p';

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF({
          orientation,
          unit: 'px',
        });

        pdf.internal.pageSize.width = componentWidth;
        pdf.internal.pageSize.height = componentHeight;

        pdf.addImage(imgData, 'PNG', 0, 0, componentWidth, componentHeight);

        const fileName = `${
          propertyData.propertyName
        } - BRRRR Cash Flow Report - ${getCurrentDateFormatted()}`;

        pdf.save(`${fileName}.pdf`);
        toast.success('PDF downloaded', { position: 'bottom-right' });
        element.style.width = 'auto';
      });
    } catch (error) {
      console.log('Failed to print the PDF', error);
      setPrinting(false);
      element.style.width = 'auto';
      toast.error('Something went wrong', { position: 'bottom-right' });
    }
  }
  // cash flow report
  const cashFlowSchema = FORMS.brrrrCashFlow.schema;

  return (
    <>
      <Helmet>
        <title>PropertyCast.io - BRRRR Analysis</title>
      </Helmet>
      {/* cash flow report */}
      <div
        id="cashflow-printable-header"
        className="grid-cols-4 p-4 hidden mb-4"
      >
        <div className="flex items-center justify-start">
          <Logo variant="dark" size="md" />
        </div>
        <div className="col-span-2 flex items-center justify-center">
          <h1 className="text-center text-4xl font-medium font-serif text-neutral-900">
            {propertyData.propertyName} - BRRRR Cash Flow Report
          </h1>
        </div>
        <div className="text-right text-md">
          <p>PropertyCast.io</p>
          <p>{formatAddress(propertyData.propertyAddress, 'short')}</p>
          <br />
          <p>{getCurrentDateFormatted()}</p>
        </div>
      </div>
      <div className="hidden bg-primary-50" id="cash-flow-report">
        <FormBuilder
          formSchema={cashFlowSchema.calculations.schema}
          formType={FORMS.rehabCashFlow.formType}
          beforeData={propertyData}
          afterData={scenarioData}
        />
      </div>

      <main>
        <div id="page-summary" className="space-y-10 my-8 bg-primary-50">
          <BRRRRSummaryOverview
            propertyData={{ ...propertyData, ...scenarioData }}
          />

          <BRRRRKeyMetrics
            propertyData={propertyData}
            scenarioData={scenarioData}
          />

          <FormBuilder
            formType={BRRRRAnalysisForm.formType}
            calculationsSchema={BRRRRAnalysisSchema}
            beforeData={propertyData}
            afterData={scenarioData}
            onReportClick={onReportClick}
          />

          <BRRRRProjectionsSummary
            propertyData={propertyData}
            scenarioData={scenarioData}
          />
        </div>

        <BackForwardButtons prev={prev} next={next} />
      </main>
    </>
  );
}
